import { wei } from '@kwenta/wei'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { suggestedDecimals } from '@kwenta/sdk/utils'
import TVChart from 'components/TVChart'
import { FlexDiv } from 'components/layout/flex'
import { selectMarketIndexPrice } from 'state/futures/common/selectors'
import {
	selectConditionalOrdersForMarket,
	selectPosition,
	selectPositionPreviewData,
	selectShowOrderLines,
	selectShowPositionLines,
	selectTradePreview,
} from 'state/futures/selectors'
import { useAppSelector } from 'state/hooks'
import { CHART_TOGGLE_HEIGHT } from './styles'

type PositionChartProps = {
	display?: boolean
}

function useSignificantChange(value: number, threshold: number): number {
	const [significantValue, setSignificantValue] = useState<number>(value)
	const lastSignificantValue = useRef<number>(value)

	useEffect(() => {
		const change: number = Math.abs(value - lastSignificantValue.current)
		let percentChange = 0

		// Avoid division by zero and handle zero change explicitly
		if (lastSignificantValue.current !== 0) {
			percentChange = (change / Math.abs(lastSignificantValue.current)) * 100
		} else if (value !== 0) {
			// Consider any change from zero to a non-zero value as significant
			percentChange = 100 // Arbitrarily large to ensure update
		}

		// Update if the percentage change exceeds the threshold
		if (percentChange >= threshold) {
			setSignificantValue(value)
			lastSignificantValue.current = value
		}
	}, [value, threshold])

	return significantValue
}
const THRESHOLD: number = 0.1 // Threshold of 0.1%

export default function PositionChart({ display = true }: PositionChartProps) {
	const position = useAppSelector(selectPosition)
	const openOrders = useAppSelector(selectConditionalOrdersForMarket, isEqual)
	const previewTrade = useAppSelector(selectTradePreview, isEqual)
	const positionPreview = useAppSelector(selectPositionPreviewData, isEqual)
	const initialPrice = useAppSelector(selectMarketIndexPrice)
	const showOrderLines = useAppSelector(selectShowOrderLines)
	const showPositionLines = useAppSelector(selectShowPositionLines)

	const [isChartReady, setIsChartReady] = useState(false)

	const liquidationPrice = useSignificantChange(
		wei(position?.details.price.liquidationPrice ?? '0').toNumber(),
		THRESHOLD
	)

	const positionSignature = useMemo(() => {
		return (
			(position?.details?.price.avgEntryPrice?.toString() ?? '') +
			(position?.details?.size.toString() ?? '')
		)
	}, [position])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const activePosition = useMemo(() => {
		if (!position) {
			return null
		}

		return {
			// As there's often a delay in subgraph sync we use the contract last
			// price until we get average price to keep it snappy on opening a position
			price: position.details?.price.avgEntryPrice ?? position.details.price.lastPrice ?? '0',
			size: position.details.size,
			liqPrice: position.details.price.liquidationPrice ?? '0',
		}
	}, [positionSignature, liquidationPrice])

	const onChartReady = useCallback(() => {
		setIsChartReady(true)
	}, [])

	const decimals = useMemo(
		() =>
			Number(initialPrice) > 100 && Number(initialPrice) < 1000
				? 3
				: suggestedDecimals(initialPrice),
		[initialPrice]
	)

	return (
		<Container $visible={isChartReady} $display={display}>
			<TVChart
				openOrders={openOrders}
				activePosition={activePosition}
				decimals={decimals}
				potentialTrade={
					previewTrade
						? {
								price: positionPreview?.avgEntryPrice.toString() ?? previewTrade.fillPrice,
								liqPrice: previewTrade.liqPrice ?? '0',
								size: previewTrade.newSize,
							}
						: null
				}
				onChartReady={onChartReady}
				showOrderLines={showOrderLines}
				showPositionLines={showPositionLines}
			/>
		</Container>
	)
}

const Container = styled(FlexDiv)<{ $visible: boolean; $display?: boolean }>`
	flex: 1;
	height: calc(100% - ${CHART_TOGGLE_HEIGHT}px);
	width: 100%;
	visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
	${(props) =>
		!props.$display &&
		css`
			display: none;
		`}
`
